import React, { useState, useMemo } from 'react';

/**
 * APEX v7.8.3 — LeftRail
 *
 * Rail vertical à gauche, symétrique au rail droit (ApexCirclesRail).
 * 2 ronds :
 *   - Mission du jour (progression)
 *   - Filtre patch (sélecteur : affiche le patch actif, clic = liste)
 *
 * Réutilise le système activeFilter existant ({ type:'patch', value:'16.10' }).
 */

function ProgressCircle({ percent, label, value, sub, color, glow, onClick, icon }) {
  const size = 110;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const clamped = Math.max(0, Math.min(100, percent || 0));
  const offset = circumference - (clamped / 100) * circumference;

  return (
    <div
      className="flex flex-col items-center gap-2 cursor-pointer group select-none"
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`${label}: ${value}`}
    >
      <div
        className="relative rounded-full transition-transform duration-200 group-hover:scale-105"
        style={{
          width: size,
          height: size,
          background: 'radial-gradient(circle, rgba(24,24,27,0.9) 0%, rgba(9,9,11,0.6) 70%)'
        }}
      >
        <svg width={size} height={size} className="-rotate-90 absolute inset-0">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke="rgb(39 39 42)" strokeWidth={stroke} />
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={offset}
            style={{
              transition: 'stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1)',
              filter: `drop-shadow(0 0 6px ${glow})`
            }} />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-0.5">
          {icon && <div className="mb-0.5">{icon}</div>}
          <span className="text-lg font-black leading-none" style={{ color }}>
            {value}
          </span>
          {sub && <span className="text-[9px] text-zinc-500 font-mono">{sub}</span>}
        </div>
      </div>
      <span className="text-[10px] uppercase tracking-[0.18em] text-zinc-400 font-bold group-hover:text-white transition">
        {label}
      </span>
    </div>
  );
}

function LeftDetailPanel({ which, data, onClose, onSelectPatch, onClearFilter, onSwapMission, onSkipMission }) {
  if (!which) return null;
  return (
    <div
      className="absolute left-full ml-4 top-0 w-72 bg-zinc-950 border border-zinc-800 shadow-2xl p-4 z-50 rounded-xl"
      style={{ animation: 'apexFadeInL 0.15s ease-out' }}
    >
      <style>{`@keyframes apexFadeInL { from { opacity: 0; transform: translateX(-8px); } to { opacity: 1; transform: translateX(0); } }`}</style>
      <button
        onClick={onClose}
        className="absolute top-2 right-2 text-zinc-600 hover:text-zinc-300 transition text-lg leading-none w-5 h-5 flex items-center justify-center"
        aria-label="Fermer"
      >×</button>

      {which === 'mission' && (
        <>
          <div className="text-[10px] uppercase tracking-[0.2em] text-cyan-400 font-bold mb-2">
            Mission du jour
          </div>
          {data.mission ? (
            <>
              <div className="text-sm font-bold text-white mb-2">{data.missionLabel}</div>
              {data.missionDesc && (
                <div className="text-[11px] text-zinc-400 mb-3">{data.missionDesc}</div>
              )}
              <div className="flex items-baseline gap-2 mb-2">
                <span className="text-2xl font-black text-cyan-400">{data.missionPercent}%</span>
                {data.missionDone && (
                  <span className="text-[10px] uppercase tracking-wider font-bold text-emerald-400">
                    Complétée ✓
                  </span>
                )}
              </div>
              <div className="h-1.5 bg-zinc-900 overflow-hidden rounded-full mb-1.5">
                <div className="h-full bg-cyan-400 rounded-full transition-all duration-700"
                  style={{ width: `${data.missionPercent}%` }} />
              </div>
              <div className="text-[10px] font-mono text-zinc-500">{data.missionProgressLabel}</div>
              {(onSwapMission || onSkipMission) && !data.missionDone && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-zinc-800/50">
                  {onSwapMission && (
                    <button
                      onClick={onSwapMission}
                      className="flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold text-zinc-400 border border-zinc-800 hover:border-cyan-400 hover:text-cyan-400 transition"
                    >
                      Changer
                    </button>
                  )}
                  {onSkipMission && (
                    <button
                      onClick={onSkipMission}
                      className="flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wider font-bold text-zinc-400 border border-zinc-800 hover:border-rose-400 hover:text-rose-400 transition"
                    >
                      Passer
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-[11px] text-zinc-500">
              Pas de mission active aujourd'hui. Elle se génère automatiquement.
            </div>
          )}
        </>
      )}

      {which === 'patch' && (
        <>
          <div className="text-[10px] uppercase tracking-[0.2em] text-fuchsia-400 font-bold mb-3">
            Filtre par patch
          </div>
          <button
            onClick={onClearFilter}
            className={`w-full text-left px-3 py-2 mb-1 text-xs font-bold border transition ${
              !data.activeValue
                ? 'border-fuchsia-400 text-fuchsia-400 bg-fuchsia-400/5'
                : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
            }`}
          >
            Tout l'historique <span className="text-zinc-600">({data.totalGames})</span>
          </button>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {data.patchList.map(({ patch, count }) => (
              <button
                key={patch}
                onClick={() => onSelectPatch(patch)}
                className={`w-full text-left px-3 py-2 text-xs font-bold border transition ${
                  data.activeValue === patch
                    ? 'border-fuchsia-400 text-fuchsia-400 bg-fuchsia-400/5'
                    : 'border-zinc-800 text-zinc-400 hover:border-zinc-600'
                }`}
              >
                Patch v{patch} <span className="text-zinc-600">({count} games)</span>
              </button>
            ))}
            {data.patchList.length === 0 && (
              <div className="text-[11px] text-zinc-600 italic px-1">
                Aucun patch détecté dans tes games
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export default function LeftRail({ games, dailyMission, activeFilter, onSetFilter, onSwapMission, onSkipMission }) {
  const [open, setOpen] = useState(null);
  const allGames = Array.isArray(games) ? games : [];

  // ── Mission du jour ──
  const mission = useMemo(() => {
    if (!dailyMission || dailyMission.status === 'expired') return null;
    const data = dailyMission.data || {};
    const isDiscipline = data.target?.maxGamesToday != null;
    const target = data.target?.games || 1;
    const prog = data.progress?.gamesContributed || 0;
    let percent, label, done;
    if (isDiscipline) {
      const max = data.target.maxGamesToday || 1;
      percent = Math.min(100, Math.round((prog / max) * 100));
      label = `${prog} / ${max} games`;
      done = dailyMission.status === 'completed';
    } else {
      percent = Math.min(100, Math.round((prog / target) * 100));
      label = `${prog} / ${target} games`;
      done = prog >= target || dailyMission.status === 'completed';
    }
    return {
      percent,
      progressLabel: label,
      title: data.label || 'Mission du jour',
      description: data.description || '',
      done
    };
  }, [dailyMission]);

  // ── Patchs disponibles ──
  const patchList = useMemo(() => {
    const counts = {};
    for (const g of allGames) {
      if (g.patch) counts[g.patch] = (counts[g.patch] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([patch, count]) => ({ patch, count }))
      .sort((a, b) => b.patch.localeCompare(a.patch, undefined, { numeric: true }));
  }, [allGames]);

  const activePatchValue =
    activeFilter?.type === 'patch' ? activeFilter.value : null;

  const detailData = {
    mission: !!mission,
    missionLabel: mission?.title || '',
    missionDesc: mission?.description || '',
    missionPercent: mission?.percent ?? 0,
    missionProgressLabel: mission?.progressLabel || '',
    missionDone: mission?.done || false,
    patchList,
    activeValue: activePatchValue,
    totalGames: allGames.length
  };

  return (
    <div
      className="hidden xl:flex fixed left-5 top-1/2 -translate-y-1/2 z-40 flex-col items-center gap-6 bg-zinc-950/85 backdrop-blur-md border border-zinc-800/70 rounded-3xl px-4 py-7 shadow-2xl"
    >
      <ProgressCircle
        percent={mission?.percent ?? 0}
        label="Mission"
        value={mission ? `${mission.percent}%` : '—'}
        sub={mission?.done ? '✓ faite' : null}
        color="#22d3ee"
        glow="rgba(34,211,238,0.55)"
        onClick={() => setOpen(open === 'mission' ? null : 'mission')}
      />
      <div className="w-10 h-px bg-zinc-800" />
      <ProgressCircle
        percent={activePatchValue ? 100 : 0}
        label="Patch"
        value={activePatchValue ? `v${activePatchValue}` : 'TOUT'}
        sub={activePatchValue ? 'filtré' : 'tous'}
        color="#e879f9"
        glow="rgba(232,121,249,0.5)"
        onClick={() => setOpen(open === 'patch' ? null : 'patch')}
      />

      {open && (
        <LeftDetailPanel
          which={open}
          data={detailData}
          onClose={() => setOpen(null)}
          onSelectPatch={(patch) => {
            onSetFilter?.({ type: 'patch', value: patch });
            setOpen(null);
          }}
          onClearFilter={() => {
            onSetFilter?.({ type: 'all', value: null });
            setOpen(null);
          }}
          onSwapMission={() => { onSwapMission?.(); setOpen(null); }}
          onSkipMission={() => { onSkipMission?.(); setOpen(null); }}
        />
      )}
    </div>
  );
}
